<template lang="pug">
#resident-list(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 利用者グループの変更

    .box-body
      span.message 移動する利用者にチェックを付けて、「右に移す→」もしくは「←元に戻す」ボタンで移動させ、最後に更新ボタンを押してください。

      .list-container.d-flex.flex-row.align-items-center.x-scroll
        .resident-group-list.d-flex.flex-column
          .list-title
            span 移動元（{{ groupName }}以外）
          .list-body
            .resident-title.d-flex.flex-row.align-items-center
              span.group-select-title 利用者グループ
              select.group-select-box(v-model="selectedGroupId")
                option(v-for="option in groupOptions" :value="option.value") {{ option.text }}
            .resident(v-for="resident in _filteredResidents" v-if="resident.display" :key="resident.id").d-flex.flex-row
              b-form-checkbox(v-model="resident.isTarget")
              span {{ resident.name }}（{{ resident.room }}）

        .resident-move-buttons.d-flex.flex-column
          button.btn.move-right-btn(@click="moveToEditTarget" type="button") 右に移す→
          button.btn.move-left-btn(@click="moveToResident" type="button") ←元に戻す

        .resident-group-list.d-flex.flex-column
          .list-title
            span 移動先（{{ groupName }}）
          .list-body
            .resident(v-for="resident in groupRegisteredResidents" :key="resident.id").d-flex.flex-row
              span {{ resident.name }}（{{ resident.room }}）
            .resident(v-for="resident in editTargetResidents" :key="resident.id").d-flex.flex-row
              b-form-checkbox(v-model="resident.isTarget")
              span {{ resident.name }}（{{ resident.room }}）

    .box-footer.with-border.register-footer
      .buttons
        button.btn.update-btn(v-if="0 < editTargetResidents.length" @click="confirmUpdate" type="button") 更新
        button.btn.update-btn.disable(v-else @click="confirmUpdate" type="button" disabled) 更新
        button.btn.cancel-btn(@click="gotoResidentGroupList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="利用者グループの所属利用者を更新しますか？" @ok="updateResidentGroup")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoResidentGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import ResidentsService, { ResidentGetModel, ResidentPatchModel } from "@/commons/services/Residents";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";

/**
 * 施設関係者グループ一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal
  }
})
export default class ResidentGroupMemberEdit extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 利用者グループID */
  groupId: number = 0;
  /** 利用者グループ名 */
  groupName: string = "";

  /** 左リスト用利用者 */
  residents: Array<ResidentItemModel> = [];
  /** フィルタリング用グループID */
  selectedGroupId: number = 0;
  /** フィルタリング用グループリストOption */
  groupOptions: Array<{ value: number; text: string }> = [];

  /** 右リスト用利用者(グループ登録済) */
  groupRegisteredResidents: Array<ResidentItemModel> = [];
  /** 右リスト用利用者(グループ未登録) */
  editTargetResidents: Array<ResidentItemModel> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### フィルタリングした左リスト用利用者を返す
   *
   * - フィルタリング用グループID=0の場合は全てを表示させる
   * - 上記以外の場合、フィルタリング用グループIDと同じグループIDのものを表示させる
   */
  get _filteredResidents(): Array<ResidentItemModel> {
    for (const resident of this.residents) {
      if (this.selectedGroupId == 0) {
        resident.display = true;
      } else if (resident.groupId == this.selectedGroupId) {
        resident.display = true;
      } else {
        resident.display = false;
      }
    }

    return this.residents;
  }

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - グループIDをURLパラメータから格納する
   * - グループ情報を取得してグループに関連する情報を作成する
   *   - グループ名を格納する
   *   - グループフィルタリング用のセレクトボックスOptionを作成する
   * - 利用者情報を取得して各リスト用情報を作成する
   *   - 既にグループに登録されていれば右リスト用利用者(グループ登録済)へ格納する
   *   - グループに登録されていばいなければ左リスト用利用者へ格納する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.groupId = Number(this.$route.params.id);

    const group: ResidentGroupsGetModel = await this.getResidentGroup();
    this.groupName = group.name;

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupOptions: Array<{ value: number; text: string }> = this.createGroupOptions(groups);
    this.groupOptions = groupOptions;

    const residents: Array<ResidentItemModel> = await this.getResidents();
    this.residents = [];
    this.groupRegisteredResidents = [];
    this.editTargetResidents = [];
    for (let resident of residents) {
      let residentItem: ResidentItemModel = Object.assign({}, resident);
      residentItem.display = true;
      residentItem.isTarget = false;
      // 電話番号が登録されていない場合が存在してしまっているので、その場合は空文字を入れる
      residentItem.tel = residentItem.tel ? residentItem.tel : "";
      if (residentItem.groupId == this.groupId) {
        this.groupRegisteredResidents.push(residentItem);
      } else {
        this.residents.push(residentItem);
      }
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### グループIDを指定して利用者グループを取得する
   */
  public async getResidentGroup(): Promise<ResidentGroupsGetModel> {
    const response: ResidentGroupsGetModel = await this.residentGroupsService.get(userStore, Number(this.$route.params.id));
    return response;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループフィルタリング用のセレクトボックスOptionを作成する
   *
   * - 「全て」のOptionを用意する
   * - グループID、グループ名でOptionを作成する
   */
  public createGroupOptions(groups: Array<ResidentGroupsGetModel>): Array<{ value: number; text: string }> {
    let groupOptions: Array<{ value: number; text: string }> = [];

    const groupOption: { value: number; text: string } = { value: 0, text: "全て" };
    groupOptions.push(groupOption);

    for (const group of groups) {
      if (group.id != this.groupId) {
        const groupOption: { value: number; text: string } = { value: group.id, text: group.name };
        groupOptions.push(groupOption);
      }
    }

    return groupOptions;
  }

  /**
   * ### 利用者を取得する
   */
  public async getResidents(): Promise<Array<ResidentItemModel>> {
    let response: Array<ResidentGetModel> = [];

    try {
      response = await this.residentsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.residentsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.residentsService.gets(userStore, params));
        }
      }
    }
    const residents: Array<ResidentItemModel> = response as Array<ResidentItemModel>;
    return residents;
  }

  /**
   * ### 利用者リストから更新対象リストに移動する
   *
   * - 移動対象ならハードコピーした利用者を更新対象リストへ格納する
   *   - 画面表示でチェックボックスのチェックをはずしたいのでisTargetをfalseにする
   * - 移動対象外ならハードコピーした利用者を新しい利用者リストへ格納する
   */
  public moveToEditTarget(): void {
    let movedResidents: Array<ResidentItemModel> = [];

    for (let resident of this.residents) {
      if (resident.isTarget) {
        let editedResidentItem: ResidentItemModel = Object.assign({}, resident);
        editedResidentItem.isTarget = false;
        this.editTargetResidents.push(editedResidentItem);
      } else {
        const movedResidentItem: ResidentItemModel = Object.assign({}, resident);
        movedResidents.push(movedResidentItem);
      }
    }

    this.residents = movedResidents;
  }

  /**
   * ### 更新対象リストから利用者リストに移動する
   *
   * - 移動対象ならハードコピーした更新対象者を利用者リストへ格納する
   *   - 画面表示でチェックボックスのチェックをはずしたいのでisTargetをfalseにする
   * - 移動対象外ならハードコピーした利用者を新しい更新対象者リストへ格納する
   */
  public moveToResident(): void {
    let movedEditTargetResidents: Array<ResidentItemModel> = [];

    for (let resident of this.editTargetResidents) {
      if (resident.isTarget) {
        let editedResidentItem: ResidentItemModel = Object.assign({}, resident);
        editedResidentItem.isTarget = false;
        this.residents.push(editedResidentItem);
      } else {
        const movedResidentItem: ResidentItemModel = Object.assign({}, resident);
        movedEditTargetResidents.push(movedResidentItem);
      }
    }

    this.editTargetResidents = movedEditTargetResidents;
  }

  /**
   * ### 更新確認モーダルを表示する
   */
  public confirmUpdate(): void {
    this.showUpdateModal = true;

    return;
  }

  /**
   * ### 更新対象の利用者のグループIDを更新する
   */
  public async updateResidentGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    for (let resident of this.editTargetResidents) {
      resident.groupId = this.groupId;
      const requestBody: ResidentPatchModel = this.createRequestBody(resident);
      await this.residentsService.patch(userStore, resident.id, requestBody);
    }

    // 利用者一覧を再読み込みさせるようstoreを更新
    let residentList: ResidentListState = needRefreshesStore.residentList;
    residentList.residentList = true;
    needRefreshesStore.setResidentList(residentList);
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 更新成功ダイアログを表示する
    this.showUpdateSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### リクエストボディを作成する
   */
  public createRequestBody(residentItem: ResidentItemModel): ResidentPatchModel {
    const requestBody: ResidentPatchModel = {
      birthday: residentItem.birthday,
      gender: residentItem.gender,
      kanaName: residentItem.kanaName ? residentItem.kanaName : "",
      name: residentItem.name,
      tel: residentItem.tel,
      roomId: residentItem.roomId ? residentItem.roomId : null,
      groupId: residentItem.groupId as number,
      inAndOutDatetimes: residentItem.inAndOutDatetimes,
      insuredNumber: residentItem.insuredNumber ? residentItem.insuredNumber : null,
      careInsuranceInsuredNumber: residentItem.careInsuranceInsuredNumber ? residentItem.careInsuranceInsuredNumber : null,
      updated: residentItem.updated,
      deleteFlag: residentItem.deleteFlag
    };

    return requestBody;
  }

  /**
   * ### 利用者グループ一覧画面に遷移する
   */
  public gotoResidentGroupList(): void {
    this.$router.push(PcUrl.ResidentGroupList);

    return;
  }
}

interface ResidentItemModel extends ResidentGetModel {
  isTarget: boolean;
  display: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#resident-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .x-scroll {
    overflow-x: auto;
    padding: 5px;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .message {
    margin: 5px 5px 12px 5px;
  }

  .resident-move-buttons {
    margin: 24px;

    .move-right-btn,
    .move-left-btn {
      width: 140px;
    }
    .move-right-btn {
      margin-bottom: 12px;
    }
  }

  .resident-group-list {
    min-width: 300px;
    width: calc(50% - 105px);
    // 縦サイズは計算して求め、出来るだけ縦サイズを大きくする
    height: calc(100vh - 291px);
    margin: 5px;

    // デフォルトフォントサイズを指定する
    font-size: 16px;
    // 外枠はここで描画させる
    border: solid 1px $border-darkgray;

    .list-title {
      padding: 5px;
      min-height: 32px;
      font-weight: bold;
      text-align: center;
      background-color: $bg-header;
      border-bottom: solid 1px $border-lightgray;
      min-height: 35px;
    }
    .list-body {
      height: calc(100vh - 300px);
      overflow-y: scroll;
      overflow-x: auto;

      .resident-title {
        height: 48px;
        background-color: $bg-header;

        .group-select-title {
          margin-right: 16px;
        }

        .group-select-box {
          //min-width: 266px;
          width: calc(100% - 130px);
          height: 34px;
          padding: 0 12px;
          border: 1px solid $form-border-color;
          border-radius: 0.25rem;
          &:focus {
            // chromeが自動的に付与するスタイルを無効化する
            outline: none;
            box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
            border: 1px solid $form-border-select-color !important;
            border-radius: 0.25rem;
          }
        }
      }

      .resident-title,
      .resident {
        padding: 8px;
        border-right: solid 1px $border-lightgray;
        border-bottom: solid 1px $border-lightgray;
      }
    }
  }
}
</style>
